import React from 'react';
import { IAuditoriaPayrollDriftResultado } from '../interfaces/IAuditoriaPayrollDrift';

interface Props {
  resultado: IAuditoriaPayrollDriftResultado;
  onVerDetalhe: () => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value?: number) =>
  `${(value || 0).toFixed(2)}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
                          'badge-light-info';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'AUSENCIA_ENCARGO')
    return <span className='badge badge-light-danger'>Ausência de Encargo</span>;
  return <span className='badge badge-light-warning'>Drift Alto</span>;
};

const AuditoriaResumoPayrollDrift: React.FC<Props> = ({ resultado, onVerDetalhe }) => {
  if (!resultado) return null;

  // COR-227: quando não há par comparável válido, o card não some silenciosamente (como
  // acontecia antes, indistinguível de "auditoria limpa") — mostra um aviso mínimo orientando o
  // usuário a abrir o detalhamento para ver quais contas foram consideradas/excluídas.
  if (resultado.itens.length === 0 && resultado.statusGeral === 'BASE_INSUFICIENTE') {
    return (
      <div className='card card-custom card-stretch mb-5 border-top border-4 border-secondary'>
        <div className='card-body py-4 d-flex justify-content-between align-items-center'>
          <div>
            <span className='fw-bolder fs-6'>Variação Móvel de Folha — Payroll Drift Analytics</span>
            <div className='text-muted fs-8 mt-1'>
              Base comparativa insuficiente — nenhum par válido entre Salários e Ordenados e conta de encargo social foi localizado.
            </div>
          </div>
          <button className='btn btn-sm btn-light-secondary' onClick={onVerDetalhe}>Ver Detalhes</button>
        </div>
      </div>
    );
  }

  if (resultado.itens.length === 0) return null;

  const top5 = [...resultado.itens]
    .sort((a, b) => b.drift - a.drift)
    .slice(0, 5);

  const statusCls =
    resultado.statusGeral === 'ERRO'   ? 'border-danger' :
    resultado.statusGeral === 'ALERTA' ? 'border-warning' :
    resultado.statusGeral === 'AVISO'  ? 'border-info' :
                                         'border-success';

  return (
    <div className={`card card-custom card-stretch mb-5 border-top border-4 ${statusCls}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-5 mb-1'>
            Variação Móvel de Folha — Payroll Drift Analytics
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s) · {resultado.totalAvisos} aviso(s)
            {resultado.impactoFinanceiroTotal > 0 && (
              <> · Passivo SELIC: <strong className='text-danger'>{formatCurrency(resultado.impactoFinanceiroTotal)}</strong></>
            )}
          </span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-light-primary' onClick={onVerDetalhe}>
            Ver Detalhes
          </button>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='row g-3 mb-4'>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Erros (Drift {'>'} 30%)</div>
              <div className='fw-bolder fs-3 text-danger'>{resultado.totalErros}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Alertas (16-30%)</div>
              <div className='fw-bolder fs-3 text-warning'>{resultado.totalAlertas}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Avisos (10-15%)</div>
              <div className='fw-bolder fs-3 text-info'>{resultado.totalAvisos}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Passivo SELIC (ERRO)</div>
              <div className='fw-bolder fs-5 text-danger'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th>Conta / Descrição</th>
                <th className='text-center'>Categoria</th>
                <th className='text-center'>Mês/Ano</th>
                <th className='text-end'>Drift</th>
                <th className='text-end'>Impacto</th>
                <th className='text-center'>Ocorrência</th>
                <th className='text-center'>Status</th>
              </tr>
            </thead>
            <tbody>
              {top5.map((item, i) => (
                <tr key={i}>
                  <td>
                    <span className='fw-bold'>{item.descricaoConta}</span>
                    <br /><span className='text-muted fs-8'>{item.contaReferencial}</span>
                  </td>
                  <td className='text-center'>
                    <span className='badge badge-light-primary'>{item.categoria}</span>
                  </td>
                  <td className='text-center fw-bold'>{item.mesAno}</td>
                  <td className='text-end fw-bolder text-danger'>{formatPercent(item.drift)}</td>
                  <td className='text-end fw-bold'>
                    {item.impactoFinanceiro > 0 ? (
                      <span className='text-danger'>{formatCurrency(item.impactoFinanceiro)}</span>
                    ) : (
                      <span className='text-muted'>—</span>
                    )}
                  </td>
                  <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
                  <td className='text-center'><StatusBadge status={item.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {resultado.itens.length > 5 && (
          <div className='text-center mt-2'>
            <button className='btn btn-sm btn-light' onClick={onVerDetalhe}>
              Ver todas as {resultado.itens.length} ocorrências
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaResumoPayrollDrift;
